import type { GridColumnVisibilityModel } from '@mui/x-data-grid';

/** Logical column groups for the sample grid (bulk show/hide in the toolbar). */
export type SampleColumnGroupId = 'substrate' | 'characterization' | 'timeline';

export type SampleColumnGroupVisibility = 'all' | 'none' | 'some';

export interface SampleColumnGroupDefinition {
  id: SampleColumnGroupId;
  /** User-visible label (safe to rename without changing persistence). */
  label: string;
  fields: readonly string[];
}

export const SAMPLE_COLUMN_GROUPS: readonly SampleColumnGroupDefinition[] = [
  {
    id: 'substrate',
    label: 'Substrate',
    fields: [
      'substrate',
      'substrateName',
      'substrateVendor',
      'substrateLot',
      'substrateOrientation',
      'substratePurity',
    ],
  },
  {
    id: 'characterization',
    label: 'Characterization',
    fields: ['characterizationTechniquesNames', 'characterizationInstrumentsNames'],
  },
  {
    id: 'timeline',
    label: 'Timeline',
    fields: ['creationDate', 'addedBy', 'lastActivity', 'lastActivityDate', 'lastChangedDate', 'lastChangedBy'],
  },
] as const;

const GROUP_BY_ID: Record<SampleColumnGroupId, SampleColumnGroupDefinition> = SAMPLE_COLUMN_GROUPS.reduce(
  (acc, g) => {
    acc[g.id] = g;
    return acc;
  },
  {} as Record<SampleColumnGroupId, SampleColumnGroupDefinition>
);

/** True if the field is visible given MUI rules (missing key means visible). */
function isFieldVisible(model: GridColumnVisibilityModel, field: string): boolean {
  return model[field] !== false;
}

/**
 * Returns whether every field in the group is shown, every field hidden, or a mix.
 */
export function getSampleColumnGroupVisibility(
  model: GridColumnVisibilityModel,
  groupId: SampleColumnGroupId
): SampleColumnGroupVisibility {
  const fields = GROUP_BY_ID[groupId]?.fields;
  if (!fields?.length) return 'none';

  let visibleCount = 0;
  for (const field of fields) {
    if (isFieldVisible(model, field)) visibleCount += 1;
  }
  if (visibleCount === 0) return 'none';
  if (visibleCount === fields.length) return 'all';
  return 'some';
}

/**
 * Sets every field in the group to visible or hidden, leaving other columns unchanged.
 */
export function applySampleColumnGroupVisibility(
  model: GridColumnVisibilityModel,
  groupId: SampleColumnGroupId,
  visible: boolean
): GridColumnVisibilityModel {
  const fields = GROUP_BY_ID[groupId]?.fields;
  if (!fields?.length) return model;

  const next: GridColumnVisibilityModel = { ...model };
  for (const field of fields) {
    next[field] = visible;
  }
  return next;
}
